'use client'

import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import HorizontalScroll, {
  HorizontalScrollComponentHandles,
} from 'src/app/ui/common/card/HorizontalScroll'
import { setParam } from 'src/helpers/router.helper'
import { formatNumber } from '../../../../helpers/global.helper'
import { TabType } from './types'

export const Tabs = <T extends string>(props: {
  tabs: TabType<T>[]
  activeTab: T
  className?: string
  additionMenu?: React.ReactNode

  setActiveTab: (id: T) => void
}) => {
  const [activeTabId, setActiveTabId] = useState(props.activeTab)
  const [tabUnderlineWidth, setTabUnderlineWidth] = useState(0)
  const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0)
  const tabsRef = useRef<HTMLDivElement[]>([])
  const scrollRef = useRef<HorizontalScrollComponentHandles>()
  const activeTabIndex = props.tabs.findIndex((tab) => activeTabId === tab.id)

  const onClick = (tabId: T) => {
    const searchParams = new URLSearchParams(window.location.search)

    window.history.pushState(
      null,
      '',
      setParam({
        searchParams,
        param: 'tab',
        value: tabId.toLocaleLowerCase(),
      }),
    )

    setActiveTabId(tabId)
    props.setActiveTab(tabId)
  }

  useEffect(() => {
    setActiveTabId(props.activeTab)
  }, [props.activeTab])

  const setTabPosition = useCallback(() => {
    const currentTab = tabsRef.current[activeTabIndex]
    setTabUnderlineLeft(currentTab?.offsetLeft || 0)
    setTabUnderlineWidth(currentTab?.clientWidth || 0)
    scrollRef.current?.updateScrollSize()
  }, [activeTabIndex, scrollRef])

  useEffect(() => {
    setTabPosition()
  }, [props.tabs, setTabPosition])

  useEffect(() => {
    setTabPosition()
    window.addEventListener('resize', setTabPosition)

    return () => window.removeEventListener('resize', setTabPosition)
  }, [activeTabIndex, setActiveTabId, setTabPosition])

  return (
    <HorizontalScroll
      ref={scrollRef}
      className={clsx(
        '[--button-bg:_theme(colors.white] dark:[--button-bg:_theme(colors.black[600])]',
        props.className,
      )}
      containerClassName="grid bg-white dark:bg-black-600 md:rounded-t-sm2"
    >
      <div className="grid relative grid-flow-col gap-x-6 items-center justify-between border-b border-gray-100 dark:border-black-500 px-3 md:px-4">
        <span
          className="absolute bottom-0 block h-0.5 bg-primary transition-all duration-300"
          style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
        />

        <div className="grid grid-flow-col gap-x-6">
          {props.tabs.map((tab, index) => (
            <div
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabsRef.current[index] = el
                }
              }}
              onClick={() => {
                onClick(tab.id)
              }}
              className={clsx(
                'py-3 grid grid-flow-col justify-start hover cursor-pointer whitespace-nowrap relative hover:text-black-1000 dark:hover:text-gray-150',
                {
                  'text-gray-500 dark:text-gray-250':
                    tab.index !== activeTabIndex,
                  'text-black-1000 dark:text-gray-150':
                    tab.index === activeTabIndex,
                },
              )}
            >
              {tab.title}

              {tab.count !== undefined && tab.count > 0 && (
                <p
                  className={clsx(
                    'text-primary text-tiny -mt-1 ml-1',
                    tab.countClassName,
                  )}
                >
                  {formatNumber(tab.count)}
                </p>
              )}
            </div>
          ))}
        </div>

        {props.additionMenu}
      </div>
    </HorizontalScroll>
  )
}
